import { IconProp } from "@fortawesome/fontawesome-svg-core";
import React, { CSSProperties } from "react";
import MeasuredValueDTO from "features/shared/dtos/MeasuredValueDTO";

import styles from "./dashboardMeasuredDataCard.module.scss";
import cardHeaderStyles from 'features/shared/components/card/cardheader.module.scss';
import PromiseLoadingSpinner from "features/shared/components/loaders/PromiseLoadingSpinner";
import MeasuredDataResultDTO from "features/shared/dtos/MeasuredDataResultDTO";
import Card from "features/shared/components/card/Card";
import CardHeader from "features/shared/components/card/CardHeader";
import Icon from "features/shared/components/icons/Icon";
import { useTranslation } from "react-i18next";
import { Link, useHistory } from "react-router-dom";
import { useInjection } from "infrastructure/ioc/ioc.react";
import IDataFilterService from "features/shared/components/dataFilter/IDataFilterService";
import { IocTypes } from "infrastructure/ioc/ioc.types";
import { DataGridId } from "features/shared/components/dataGrid/DataGridId";
import { CommodityDetail } from "features/contracts/enums/commodityDetail";
import MeasuredDataGraph from "features/shared/components/graphs/MeasuredDataGraph";
import { getGraphLabel } from "features/shared/components/graphs/measuredDataGraphHelper";
import { getMeasuredDataType } from "features/shared/components/measuredData/measuredDataHelper";
import { getMeasuredDataHistoryRoute } from "features/shared/helpers/routeHelper";
import colors from 'styles/colors.module.scss';
import { ColorClassSuffix } from "features/shared/enums/colorClassSuffix";
import CommodityIcon from "features/shared/components/icons/CommodityIcon";

interface IProps {
  title: string;
  icon?: IconProp;
  iconColor?: string;
  background: string;
  realData: MeasuredDataResultDTO;
  planData?: MeasuredDataResultDTO;
  commodityDetail: CommodityDetail;
  isLoadedData: boolean;
  loaderArea?: string;
  headerClassName?: string;
  isProduction?: boolean;
  renderNoGraphContent?: () => JSX.Element;
}

const DashboardMeasuredDataCard: React.FC<IProps> = (props) => {
  // translation
  const tLabels = useTranslation("dashboard\\dashboard").t;
  const tShared = useTranslation("shared\\components").t;

  // DI
  const dataFilterService = useInjection<IDataFilterService>(IocTypes.DataFilterService);

  // history
  const history = useHistory();

  // props
    const { title, icon, iconColor, background, realData, planData, loaderArea, isLoadedData, headerClassName, commodityDetail, isProduction, renderNoGraphContent } = props;

  // helpers
  const getUnit = (consumption: MeasuredValueDTO[]) => {
    let item = consumption?.length > 0 ? consumption[0] : null;
    return item !== null ? `[${item.unit}]` : "";
  };

  const getColor = (): string => {
    switch (commodityDetail) {
      case "ElectricityCustomer":
      case "ElectricitySupplier":
        return isProduction
          ? colors[ColorClassSuffix.Production]
          : colors[ColorClassSuffix.Electricity];
      case "GasCustomer":
      case "GasSupplier":
        return colors[ColorClassSuffix.Gas];
      case "HeatCustomer":
        return colors[ColorClassSuffix.Heat];
      case "eMobility":
        return colors[ColorClassSuffix.Emobility];
      default:
        return colors[ColorClassSuffix.Default];
    }
  };

  const getIconColor = (color?: string): CSSProperties | undefined => {
    if (color) {
      return {
        color: color
      }
    }
  };

  // handlers
  const titleClickHandler = () => { 
    dataFilterService.storeSetParams(DataGridId.consumptionHistoryGrid, [
      { key: "CommodityDetail", value: commodityDetail },
    ]);
  }

  const graphClickHandler = () => { 
    dataFilterService.storeSetParams(DataGridId.consumptionHistoryGrid, [
      { key: "CommodityDetail", value: commodityDetail },
    ]);

    history.push(getMeasuredDataHistoryRoute(commodityDetail, isProduction));
  }

  // sub-renders
  const renderGraph = () => {
    if (renderNoGraphContent) {
      return renderNoGraphContent();
    }
    else if (isLoadedData && realData) {
      return (
        <MeasuredDataGraph
          graphType="bar"
          quotationType={realData.quotationType!}
          realValues={realData.measuredValues!}
          realValuesColor={getColor()}
          realValuesLabel={getGraphLabel(
            tShared,
            commodityDetail,
            "real",
            isProduction,
            planData !== undefined
          )}
          planValues={planData ? planData.measuredValues! : undefined}
          planValuesLabel={getGraphLabel(
            tShared,
            commodityDetail,
            "plan",
            isProduction,
            planData !== undefined
          )}
          containerClass={styles.chartContainer}
          showLegend
          onBarGraphClick={graphClickHandler}
        />
      );
    } else if (isLoadedData) {
      return renderNoContentInfo();
    }
  }

  const renderNoContentInfo = () => {
    return (
      <div className="d-flex justify-content-center pt-3 text-muted font-italic">
        {tLabels("dashboardMeasuredDataCard.noRecordsFound")}
      </div>
    );    
  }

  const renderCardSubtitel = () => { 
    const dataTypeKey = getMeasuredDataType(commodityDetail, isProduction);
    const translation = tLabels(`dashboardMeasuredDataCard.cardHeaderSubtitle.${dataTypeKey}`);

    if (translation) {
      return (
        <span className={`d-block ${styles.cardHeaderSubtitle}`}>
          {translation}
        </span>
      );
    }
  }

  // render
  return (
    <Card className={styles.card}>
      <CardHeader background={background}>
        <div className={`${cardHeaderStyles.cardHeaderItem} ${styles.cardHeaderr}`}>
          <Link 
            to={getMeasuredDataHistoryRoute(commodityDetail, isProduction)} 
            className={`d-flex align-items-center text-decoration-none ${styles.cardHeader}`}
            onClick={titleClickHandler}
          >
            <span className="mr-2" style={getIconColor(props.iconColor)}>
                <CommodityIcon commodityDetail={commodityDetail} isProduction={isProduction} />
            </span>
            <h2 className={cardHeaderStyles.cardTitle}>
              <span className="d-block">
                {title}
                <small className="ml-2">{getUnit(realData.measuredValues!)}</small>
              </span>
              {renderCardSubtitel()}
            </h2>
          </Link>
        </div>
      </CardHeader>
      <div className={`card-body ${styles.cardBody}`}>
        {renderGraph()}  
        <PromiseLoadingSpinner area={loaderArea} />
      </div>  
    </Card>
  );
};

export default DashboardMeasuredDataCard;
